import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ChevronRight,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Filter,
  Trash2,
  X,
  Image as ImageIcon,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useOrgInventory, fetchOrgInventoryImage, type OrgInventoryItemInput } from '../../hooks/useOrgInventory';
import { InventoryItemImage } from '../../components/ui/InventoryItemImage';
import { useOrgResidentialRooms } from '../../hooks/useResidentialRooms';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { OrgInventoryItem, OrgResidentialRoom, AddEquipmentEntry, QuoteType, ResidentialItemType } from '../../types/admin';
import { isPricedCategory, isZeroVolumeMaterial, formatUnitPricing, parseUnitCostInput, parseRentalDaysInput } from '../../lib/inventoryPricing';
import { CustomDropdown, SearchableDropdown } from '../../components/ui/CustomDropdown';
import { InventoryFilterSheet, ActiveInventoryFilterPills, countActiveInventoryFilters, type InventoryFilters } from '../../components/admin/InventoryFilterSheet';
import { InventoryTable, InventoryViewSwitch, readStoredInventoryView, storeInventoryView, type InventoryView } from '../../components/admin/InventoryTable';

// ── Constants ──────────────────────────────────────────────────────────────

const ITEM_TYPES = ['Casegoods', 'Modular Component'] as const;
type ItemType = (typeof ITEM_TYPES)[number];

const TYPE_ORDER: Record<string, number> = {
  'Casegoods': 0,
  'Modular Component': 1,
  'Modular Equipment': 2,
};

function typeBadgeClass(type: string): string {
  switch (type) {
    case 'Casegoods': return 'bg-plum/10 text-plum';
    case 'Modular Component': return 'bg-ember/10 text-ember';
    case 'Modular Equipment': return 'bg-midnight/10 text-midnight';
    default: return 'bg-stone/10 text-slate';
  }
}

const RESIDENTIAL_TYPE_LABELS: Record<ResidentialItemType, string> = {
  customer_item: 'Customer Item',
  packing_item: 'Packing Item',
  equipment: 'Equipment',
};

function residentialTypeBadgeClass(type: ResidentialItemType): string {
  switch (type) {
    case 'customer_item': return 'bg-plum/10 text-plum';
    case 'packing_item': return 'bg-ember/10 text-ember';
    case 'equipment': return 'bg-midnight/10 text-midnight';
  }
}

// ── Image helpers (same as MasterInventoryPage) ────────────────────────────

const MAX_IMAGE_DATA_URL_LENGTH = 950_000;

function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(objectUrl); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Failed to load image')); };
    img.src = objectUrl;
  });
}

async function resizeAndEncodeImage(file: File): Promise<string> {
  const img = await loadImageElement(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const MAX_DIM = 1600;
  let width = img.naturalWidth;
  let height = img.naturalHeight;
  if (width > MAX_DIM || height > MAX_DIM) {
    const scale = MAX_DIM / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  let quality = 0.85;
  for (;;) {
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (dataUrl.length <= MAX_IMAGE_DATA_URL_LENGTH) return dataUrl;
    if (quality > 0.35) {
      quality = Math.max(0.35, quality - 0.15);
    } else {
      width = Math.round(width * 0.75);
      height = Math.round(height * 0.75);
      quality = 0.75;
      if (width < 100) return canvas.toDataURL('image/jpeg', 0.35);
    }
  }
}

// ── Field label ────────────────────────────────────────────────────────────

function FieldLabel({
  htmlFor,
  children,
  optional,
}: {
  htmlFor: string;
  children: React.ReactNode;
  optional?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} className="block text-[11px] font-bold uppercase tracking-widest text-slate">
      {children}
      {!optional && <span className="text-rose ml-0.5">*</span>}
      {optional && <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>}
    </label>
  );
}

// ── Add Equipment row ──────────────────────────────────────────────────────

interface AddEquipmentRowProps {
  entry: AddEquipmentEntry;
  allItems: OrgInventoryItem[];
  excludeId?: string;
  quoteType?: QuoteType;
  onChange: (entry: AddEquipmentEntry) => void;
  onRemove: () => void;
}

function AddEquipmentRow({ entry, allItems, excludeId, quoteType, onChange, onRemove }: AddEquipmentRowProps) {
  const options = allItems
    .filter(i => i.id !== excludeId)
    .filter(i => {
      if (quoteType === 'residential') return i.quoteType === 'residential';
      return (i.quoteType ?? 'commercial') === 'commercial';
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(i => ({ value: i.id, label: i.name }));

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <SearchableDropdown
          options={options}
          value={entry.itemId}
          onChange={v => {
            const selected = allItems.find(i => i.id === v);
            if (selected) onChange({ ...entry, itemId: selected.id, itemName: selected.name });
          }}
          placeholder="Select item…"
        />
      </div>
      <input
        type="number"
        min="0"
        step="any"
        value={entry.quantity}
        onChange={e => {
          const raw = e.target.value;
          const val = parseFloat(raw);
          if (isNaN(val)) { onChange({ ...entry, quantity: 0 }); return; }
          const rounded = Math.round(val * 10000) / 10000;
          onChange({ ...entry, quantity: rounded });
        }}
        placeholder="Qty"
        className="w-20 h-11 px-2 rounded-lg border border-stone bg-white
                   text-small text-midnight placeholder:text-slate text-center
                   focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                   transition-colors duration-150"
      />
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg border border-stone
                   text-slate hover:border-rose hover:text-rose active:bg-mist
                   transition-colors duration-150"
        aria-label="Remove row"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ── Inventory Item Form ────────────────────────────────────────────────────

interface InventoryItemFormProps {
  initial?: OrgInventoryItem;
  orgId: string;
  allItems: OrgInventoryItem[];
  rooms?: OrgResidentialRoom[];
  onClose: () => void;
  /** Scroll the form to this section once it opens (used by the table's Add Equipment cell). */
  scrollTo?: 'addEquipment';
  onSave: (input: OrgInventoryItemInput) => Promise<unknown>;
}

function InventoryItemForm({ initial, orgId, allItems, rooms, onClose, scrollTo, onSave }: InventoryItemFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<string>(initial?.type ?? 'Casegoods');
  const [quoteType, setQuoteType] = useState<QuoteType>(initial?.quoteType ?? 'commercial');
  const [kz, setKz] = useState<string>(initial?.kz != null ? String(initial.kz) : '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [imageBase64, setImageBase64] = useState(initial?.imageBase64 ?? '');
  // Items from the list endpoint carry `hasImage` instead of the image itself —
  // pull the current image in so the preview (and "keep as is") works.
  const imageTouchedRef = useRef(false);
  useEffect(() => {
    if (!initial?.hasImage || initial.imageBase64 || !orgId) return;
    let cancelled = false;
    fetchOrgInventoryImage(orgId, initial.id)
      .then(img => { if (!cancelled && img && !imageTouchedRef.current) setImageBase64(img); })
      .catch(() => { /* preview stays empty; saving leaves the image untouched */ });
    return () => { cancelled = true; };
  }, [initial?.id, initial?.hasImage, initial?.imageBase64, orgId]);
  const [additionalManHours, setAdditionalManHours] = useState<string>(
    initial?.additionalManHours != null ? String(initial.additionalManHours) : '',
  );
  const [isAccessorial, setIsAccessorial] = useState(initial?.isAccessorial ?? false);
  const [accessorialLabel, setAccessorialLabel] = useState(initial?.accessorialLabel ?? '');
  const [accessorialOriginManHours, setAccessorialOriginManHours] = useState<string>(
    initial?.accessorialOriginManHours != null ? String(initial.accessorialOriginManHours) : '',
  );
  const [accessorialDestinationManHours, setAccessorialDestinationManHours] = useState<string>(
    initial?.accessorialDestinationManHours != null ? String(initial.accessorialDestinationManHours) : '',
  );
  const [addEquipment, setAddEquipment] = useState<AddEquipmentEntry[]>(initial?.addEquipment ?? []);
  // Residential fields
  const [moveUnits, setMoveUnits] = useState<string>(initial?.moveUnits != null ? String(initial.moveUnits) : '');
  const [cubicFeet, setCubicFeet] = useState<string>(initial?.cubicFeet != null ? String(initial.cubicFeet) : '');
  const [weightInPounds, setWeightInPounds] = useState<string>(initial?.weightInPounds != null ? String(initial.weightInPounds) : '');
  const [bulky, setBulky] = useState(initial?.bulky ?? false);
  const [requiredDisassembly, setRequiredDisassembly] = useState(initial?.requiredDisassembly ?? false);
  // Category is required; new items start as Customer Item.
  const [residentialType, setResidentialType] = useState<ResidentialItemType>(initial?.residentialType ?? 'customer_item');
  const [roomId, setRoomId] = useState<string>(initial?.roomId ?? '');
  const [unitCost, setUnitCost] = useState<string>(initial?.unitCost != null ? String(initial.unitCost) : '');
  const [rentalDays, setRentalDays] = useState<string>(initial?.rentalDays != null ? String(initial.rentalDays) : '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Jump to the requested section after the modal paints.
  useEffect(() => {
    if (!scrollTo) return;
    const raf = requestAnimationFrame(() => {
      document.getElementById('oinv-add-equipment')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [scrollTo]);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const isResidential = quoteType === 'residential';

  async function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeAndEncodeImage(file);
      imageTouchedRef.current = true;
      setImageBase64(dataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read image file.');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required.'); return; }
    if (!residentialType) { setError('Category is required.'); return; }
    if (isPricedCategory(residentialType) && parseRentalDaysInput(rentalDays) !== null && parseUnitCostInput(unitCost) === null) {
      setError('Unit cost is required when a rental length is set.'); return;
    }

    if (isResidential) {
      // Residential validation — no kz/type required
      for (const row of addEquipment) {
        if (!row.itemId) { setError('Each "Add Equipment" row must have an item selected.'); return; }
        if (row.quantity <= 0) { setError('Equipment quantities must be greater than 0.'); return; }
      }
      const validEquipment = addEquipment.filter(e => e.itemId);
      setError('');
      setSaving(true);
      try {
        const moveUnitsNum = moveUnits ? parseFloat(moveUnits) : undefined;
        const cubicFeetNum = cubicFeet ? parseFloat(cubicFeet) : undefined;
        const weightNum = weightInPounds ? parseFloat(weightInPounds) : undefined;
        await onSave({
          name: name.trim(),
          type: 'Casegoods', // default for residential
          kz: 0,
          description: description.trim() || undefined,
          notes: notes.trim() || undefined,
          imageBase64: imageBase64 || undefined,
          quoteType: 'residential',
          moveUnits: moveUnitsNum && !isNaN(moveUnitsNum) ? moveUnitsNum : undefined,
          cubicFeet: cubicFeetNum && !isNaN(cubicFeetNum) ? cubicFeetNum : undefined,
          weightInPounds: weightNum && !isNaN(weightNum) ? weightNum : undefined,
          bulky,
          requiredDisassembly,
          residentialType,
          roomId: roomId || undefined,
          unitCost: isPricedCategory(residentialType) ? parseUnitCostInput(unitCost) : null,
          rentalDays: isPricedCategory(residentialType) ? parseRentalDaysInput(rentalDays) : null,
          addEquipment: validEquipment.length > 0 ? validEquipment : undefined,
        });
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save item.');
        setSaving(false);
      }
      return;
    }

    // Commercial validation
    // KZ is optional: blank saves as 0
    const kzNum = kz.trim() ? parseFloat(kz) : 0;
    if (isNaN(kzNum) || kzNum < 0) { setError('KZ must be a valid non-negative number.'); return; }
    for (const row of addEquipment) {
      if (!row.itemId) { setError('Each "Add Equipment" row must have an item selected.'); return; }
      if (row.quantity <= 0) { setError('Equipment quantities must be greater than 0.'); return; }
    }
    if (isAccessorial) {
      if (!accessorialLabel.trim()) { setError('Accessorial label is required when accessorial service is enabled.'); return; }
      const originNum = accessorialOriginManHours ? parseFloat(accessorialOriginManHours) : 0;
      const destNum = accessorialDestinationManHours ? parseFloat(accessorialDestinationManHours) : 0;
      if (isNaN(originNum) || originNum < 0) { setError('Origin man hours must be a valid non-negative number.'); return; }
      if (isNaN(destNum) || destNum < 0) { setError('Destination man hours must be a valid non-negative number.'); return; }
    } else {
      const manHoursNum = additionalManHours ? parseFloat(additionalManHours) : undefined;
      if (additionalManHours && (isNaN(manHoursNum!) || manHoursNum! < 0)) {
        setError('Additional Man Hours must be a valid non-negative number.');
        return;
      }
    }
    setError('');
    setSaving(true);
    try {
      const manHoursNum = !isAccessorial && additionalManHours ? parseFloat(additionalManHours) : undefined;
      const originNum = isAccessorial && accessorialOriginManHours ? parseFloat(accessorialOriginManHours) : undefined;
      const destNum = isAccessorial && accessorialDestinationManHours ? parseFloat(accessorialDestinationManHours) : undefined;
      await onSave({
        name: name.trim(),
        type: type as OrgInventoryItemInput['type'],
        kz: kzNum,
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
        imageBase64: imageBase64 || undefined,
        additionalManHours: isAccessorial ? undefined : manHoursNum,
        isAccessorial: isAccessorial || undefined,
        accessorialLabel: isAccessorial ? accessorialLabel.trim() : undefined,
        accessorialOriginManHours: isAccessorial ? (originNum ?? 0) : undefined,
        accessorialDestinationManHours: isAccessorial ? (destNum ?? 0) : undefined,
        addEquipment: addEquipment.filter(e => e.itemId).length > 0
          ? addEquipment.filter(e => e.itemId)
          : undefined,
        quoteType,
        residentialType,
        unitCost: isPricedCategory(residentialType) ? parseUnitCostInput(unitCost) : null,
        rentalDays: isPricedCategory(residentialType) ? parseRentalDaysInput(rentalDays) : null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save item.');
      setSaving(false);
    }
  }

  return (
    /* Mobile: bottom sheet. md+: centered dialog. */
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[92vh] overflow-y-auto
                      md:rounded-2xl md:max-w-lg md:w-full md:max-h-[85vh] md:pb-6">
        {/* Drag handle — mobile only */}
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">
            {initial ? 'Edit Item' : 'Add Item'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/*
          Mobile: single-column.
          md+: 2-column grid — KZ warning (full), KZ (left) + Man Hours (right); all else full.
        */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-x-5 md:gap-y-4">
          {error && (
            <div className="md:col-span-2 flex items-start gap-2 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30">
              <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
              <p className="text-small text-rose">{error}</p>
            </div>
          )}

          {/* Name — full width */}
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <FieldLabel htmlFor="oinv-name">Name</FieldLabel>
            <input
              id="oinv-name"
              type="text"
              autoComplete="off"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Standard Desk"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Move type — full width */}
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <FieldLabel htmlFor="oinv-quotetype">Move type</FieldLabel>
            <div className="flex bg-mist rounded-lg p-1 gap-1">
              {(['commercial', 'residential'] as const).map(qt => (
                <button
                  key={qt}
                  type="button"
                  onClick={() => setQuoteType(qt)}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                    ${quoteType === qt
                      ? 'bg-white text-midnight shadow-brand-sm'
                      : 'text-slate hover:text-midnight'
                    }`}
                >
                  {qt === 'commercial' ? 'Commercial' : 'Residential'}
                </button>
              ))}
            </div>
          </div>

          {/* Item Category + Type — side-by-side dropdowns on every viewport */}
          <div className="grid grid-cols-[1fr_1.25fr] gap-3 md:col-span-2">
            <div className={`flex flex-col gap-1.5 justify-end ${isResidential ? 'col-span-2' : ''}`}>
              <FieldLabel htmlFor="oinv-category">Category</FieldLabel>
              <CustomDropdown<ResidentialItemType>
                id="oinv-category"
                value={residentialType}
                onChange={setResidentialType}
                options={[
                  { value: 'customer_item', label: 'Customer Item' },
                  { value: 'packing_item', label: 'Packing Item' },
                  { value: 'equipment', label: 'Equipment' },
                ]}
              />
            </div>
            {!isResidential && (
              <div className="flex flex-col gap-1.5 justify-end">
                <FieldLabel htmlFor="oinv-type">Type</FieldLabel>
                <CustomDropdown
                  id="oinv-type"
                  value={type}
                  onChange={setType}
                  options={ITEM_TYPES.map(t => ({ value: t, label: t }))}
                />
              </div>
            )}
          </div>

          {/* Pricing — packing items and equipment only; directly below the category/type row */}
          {isPricedCategory(residentialType) && (
            <>
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="oinv-unit-cost" optional>Unit Cost ($)</FieldLabel>
                <input
                  id="oinv-unit-cost"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={unitCost}
                  onChange={e => setUnitCost(e.target.value)}
                  placeholder="e.g. 3.00"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="oinv-rental-days" optional>Rental Length (days)</FieldLabel>
                <input
                  id="oinv-rental-days"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  value={rentalDays}
                  onChange={e => setRentalDays(e.target.value)}
                  placeholder="Blank if purchased"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>
              <p className="text-[12px] text-slate -mt-2 md:col-span-2">
                {formatUnitPricing(parseUnitCostInput(unitCost), parseRentalDaysInput(rentalDays))
                  ?? 'Leave rental length blank for items that are purchased or consumed rather than returned.'}
              </p>
            </>
          )}

          {/* Room selector — residential only, right after quote type */}
          {isResidential && rooms && rooms.length > 0 && (
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <FieldLabel htmlFor="oinv-room" optional>Room</FieldLabel>
              <SearchableDropdown
                id="oinv-room"
                value={roomId}
                onChange={setRoomId}
                placeholder="No room assigned"
                options={[
                  { value: '', label: 'No room assigned' },
                  ...rooms.map(r => ({ value: r.id, label: r.name })),
                ]}
              />
            </div>
          )}

          {/* ── Commercial-specific fields ── */}
          {!isResidential && (
            <>
              {/* KZ warning — edit mode only, full width */}
              {initial && (
                <div className="md:col-span-2 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-ember/8 border border-ember/25">
                  <AlertTriangle size={14} className="shrink-0 text-ember mt-0.5" />
                  <p className="text-[12px] text-ember leading-snug">
                    Changing the KZ value will affect how long this item's moves are estimated. Inaccurate KZ values lead to inaccurate quotes.
                  </p>
                </div>
              )}

              {/* KZ — left column */}
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="oinv-kz" optional>KZ Value</FieldLabel>
                <input
                  id="oinv-kz"
                  type="number"
                  min="0"
                  step="0.01"
                  value={kz}
                  onChange={e => setKz(e.target.value)}
                  placeholder="0"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
                {isZeroVolumeMaterial({ residentialType, kz: Number(kz) || 0 }) && (
                  <p className="text-[12px] text-slate">
                    With a KZ of 0, estimators add this item from Materials, not the Casegoods list.
                  </p>
                )}
              </div>

              {/* Additional Man Hours — right column (only when NOT accessorial) */}
              {!isAccessorial && (
                <div className="flex flex-col gap-1.5">
                  <FieldLabel htmlFor="oinv-manhours" optional>Additional Man Hours</FieldLabel>
                  <input
                    id="oinv-manhours"
                    type="number"
                    min="0"
                    step="0.01"
                    value={additionalManHours}
                    onChange={e => setAdditionalManHours(e.target.value)}
                    placeholder="e.g. 0.5"
                    className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                               text-small text-midnight placeholder:text-slate
                               focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                               transition-colors duration-150"
                  />
                </div>
              )}

              {/* Accessorial Service toggle — full width */}
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAccessorial}
                    onChange={e => setIsAccessorial(e.target.checked)}
                    className="w-5 h-5 rounded border-stone text-plum focus:ring-plum/20"
                  />
                  <span className="text-small text-midnight font-medium">Accessorial Service</span>
                </label>
              </div>

              {/* Accessorial fields — only when toggle is ON */}
              {isAccessorial && (
                <>
                  <div className="flex flex-col gap-1.5 md:col-span-2">
                    <FieldLabel htmlFor="oinv-acc-label">Accessorial Label</FieldLabel>
                    <input
                      id="oinv-acc-label"
                      type="text"
                      autoComplete="off"
                      value={accessorialLabel}
                      onChange={e => setAccessorialLabel(e.target.value)}
                      placeholder='e.g. "Glass Bonding", "Special Crating"'
                      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="oinv-acc-origin">Origin Man Hours</FieldLabel>
                    <input
                      id="oinv-acc-origin"
                      type="number"
                      min="0"
                      step="0.01"
                      value={accessorialOriginManHours}
                      onChange={e => setAccessorialOriginManHours(e.target.value)}
                      placeholder="0"
                      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="oinv-acc-dest">Destination Man Hours</FieldLabel>
                    <input
                      id="oinv-acc-dest"
                      type="number"
                      min="0"
                      step="0.01"
                      value={accessorialDestinationManHours}
                      onChange={e => setAccessorialDestinationManHours(e.target.value)}
                      placeholder="0"
                      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                  </div>
                </>
              )}

            </>
          )}

          {/* ── Residential-specific fields ── */}
          {isResidential && (
            <>
              {/* Move Units — left column */}
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="oinv-move-units" optional>Move Units</FieldLabel>
                <input
                  id="oinv-move-units"
                  type="number"
                  min="0"
                  step="0.01"
                  value={moveUnits}
                  onChange={e => setMoveUnits(e.target.value)}
                  placeholder="e.g. 1.0"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>

              {/* Cubic Feet — right column */}
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="oinv-cubic-feet" optional>Cubic Feet</FieldLabel>
                <input
                  id="oinv-cubic-feet"
                  type="number"
                  min="0"
                  step="0.1"
                  value={cubicFeet}
                  onChange={e => setCubicFeet(e.target.value)}
                  placeholder="e.g. 12.5"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>

              {/* Weight — left column */}
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="oinv-weight" optional>Weight (lbs)</FieldLabel>
                <input
                  id="oinv-weight"
                  type="number"
                  min="0"
                  step="0.1"
                  value={weightInPounds}
                  onChange={e => setWeightInPounds(e.target.value)}
                  placeholder="e.g. 50"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>

              {/* Toggles — right column */}
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bulky}
                    onChange={e => setBulky(e.target.checked)}
                    className="w-5 h-5 rounded border-stone text-plum focus:ring-plum/20"
                  />
                  <span className="text-small text-midnight font-medium">Bulky item</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requiredDisassembly}
                    onChange={e => setRequiredDisassembly(e.target.checked)}
                    className="w-5 h-5 rounded border-stone text-plum focus:ring-plum/20"
                  />
                  <span className="text-small text-midnight font-medium">Requires disassembly</span>
                </label>
              </div>
            </>
          )}

          {/* Description — full width */}
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <FieldLabel htmlFor="oinv-description" optional>Description</FieldLabel>
            <textarea
              id="oinv-description"
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of the item…"
              className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate resize-none
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Notes — full width */}
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <FieldLabel htmlFor="oinv-notes" optional>Notes</FieldLabel>
            <textarea
              id="oinv-notes"
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Internal notes for estimators…"
              className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate resize-none
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Image — full width */}
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <FieldLabel htmlFor="oinv-image" optional>Image</FieldLabel>
            {imageBase64 ? (
              <div className="flex items-center gap-3">
                <img src={imageBase64} alt="Item preview" className="w-16 h-16 rounded-lg object-cover border border-stone" />
                <div className="flex flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    className="h-8 px-3 rounded-lg border border-stone text-[13px] font-semibold text-slate
                               hover:border-plum hover:text-plum active:bg-mist transition-colors"
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    onClick={() => { imageTouchedRef.current = true; setImageBase64(''); }}
                    className="h-8 px-3 rounded-lg border border-stone text-[13px] font-semibold text-slate
                               hover:border-rose hover:text-rose active:bg-mist transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="h-11 rounded-lg border-2 border-dashed border-stone
                           flex items-center justify-center gap-2
                           text-small font-semibold text-slate
                           hover:border-plum hover:text-plum active:bg-mist
                           transition-colors duration-150"
              >
                <ImageIcon size={16} />
                Upload Image
              </button>
            )}
            <input
              ref={imageInputRef}
              id="oinv-image"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
          </div>

          {/* Add Equipment — full width */}
          <div id="oinv-add-equipment" className="flex flex-col gap-2 md:col-span-2 scroll-mt-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
              Add Equipment
              <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>
            </p>
            <p className="text-[12px] text-slate leading-snug -mt-1">
              Items automatically added when this item is included in a job.
            </p>
            {addEquipment.length > 0 && (
              <div className="flex flex-col gap-2">
                {addEquipment.map((entry, idx) => (
                  <AddEquipmentRow
                    key={idx}
                    entry={entry}
                    allItems={allItems}
                    excludeId={initial?.id}
                    quoteType={quoteType}
                    onChange={updated => setAddEquipment(prev => prev.map((e, i) => (i === idx ? updated : e)))}
                    onRemove={() => setAddEquipment(prev => prev.filter((_, i) => i !== idx))}
                  />
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => setAddEquipment(prev => [...prev, { itemId: '', itemName: '', quantity: 1 }])}
              disabled={allItems.filter(i => i.id !== initial?.id).length === 0}
              className="h-9 rounded-lg border border-dashed border-stone
                         flex items-center justify-center gap-1.5
                         text-[13px] font-semibold text-slate
                         hover:border-plum hover:text-plum active:bg-mist
                         disabled:opacity-40 disabled:cursor-not-allowed
                         transition-colors duration-150"
            >
              <Plus size={13} />
              Add Row
            </button>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="md:col-span-2 h-11 rounded-lg bg-plum text-white text-[13px] font-semibold
                       flex items-center justify-center gap-2
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150 mt-1"
          >
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Item'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Inventory Item Card ────────────────────────────────────────────────────

function InventoryItemCard({
  item,
  orgId,
  rooms,
  onEdit,
  onDelete,
}: {
  item: OrgInventoryItem;
  orgId: string;
  rooms?: OrgResidentialRoom[];
  onEdit: () => void;
  onDelete: () => void;
}) {
  const isResidential = item.quoteType === 'residential';
  const roomName = isResidential && item.roomId && rooms
    ? rooms.find(r => r.id === item.roomId)?.name
    : undefined;
  const pricingLabel = formatUnitPricing(item.unitCost, item.rentalDays);

  return (
    <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
      <div className="flex items-start gap-3">
        <InventoryItemImage
          orgId={orgId}
          item={item}
          alt={item.name}
          wrapperClassName="shrink-0"
          className="w-10 h-10 rounded-lg object-cover border border-stone"
          fallback={(
            <div className="w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
              <Package size={18} className="text-plum" />
            </div>
          )}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-small text-midnight leading-snug">{item.name}</p>
            {isResidential ? (
              item.residentialType && (
                <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md
                  ${residentialTypeBadgeClass(item.residentialType)}`}>
                  {RESIDENTIAL_TYPE_LABELS[item.residentialType]}
                </span>
              )
            ) : (
              <>
                <span
                  className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md
                    ${typeBadgeClass(item.type as ItemType)}`}
                >
                  {item.type}
                </span>
                {item.residentialType && (
                  <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md
                    ${residentialTypeBadgeClass(item.residentialType)}`}>
                    {RESIDENTIAL_TYPE_LABELS[item.residentialType]}
                  </span>
                )}
              </>
            )}
          </div>
          {isResidential ? (
            <div className="flex items-center gap-2 flex-wrap text-[12px] text-slate mt-0.5">
              {item.moveUnits != null && <span>MU: <span className="font-semibold text-midnight font-mono">{item.moveUnits}</span></span>}
              {item.weightInPounds != null && <span>{item.moveUnits != null && '·'} {item.weightInPounds} lbs</span>}
              {item.cubicFeet != null && <span>· {item.cubicFeet} ft³</span>}
              {item.bulky && <span className="text-[10px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-ember/10 text-ember">Bulky</span>}
              {item.requiredDisassembly && <span className="text-[10px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-midnight/10 text-midnight">Disassembly</span>}
            </div>
          ) : (item.isAccessorial || item.additionalManHours != null) && (
            <p className="text-[12px] text-slate mt-0.5">
              {item.isAccessorial ? (
                <span className="ml-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-plum/10 text-plum">Accessorial</span>
                  <span className="ml-1">{item.accessorialLabel}</span>
                  <span className="ml-1 text-slate">
                    ({item.accessorialOriginManHours ?? 0}h origin, {item.accessorialDestinationManHours ?? 0}h dest)
                  </span>
                </span>
              ) : item.additionalManHours != null && (
                <span className="ml-2">+{item.additionalManHours} hr{item.additionalManHours !== 1 ? 's' : ''}</span>
              )}
            </p>
          )}
          {pricingLabel && (
            <p className="text-[12px] text-slate mt-0.5">
              <span className="font-semibold text-midnight font-mono">{pricingLabel}</span>
            </p>
          )}
          {roomName && (
            <p className="text-[11px] text-slate mt-0.5">Room: {roomName}</p>
          )}
          {item.description && (
            <p className="text-[13px] text-slate mt-1 leading-snug line-clamp-2">{item.description}</p>
          )}
          {item.addEquipment && item.addEquipment.length > 0 && (
            <p className="text-[12px] text-slate mt-1">
              Also adds: {item.addEquipment.map(e => `${e.quantity}× ${e.itemName}`).join(', ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:border-plum hover:text-plum active:bg-mist
                       transition-colors duration-150"
            aria-label={`Edit ${item.name}`}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:border-rose hover:text-rose active:bg-mist
                       transition-colors duration-150"
            aria-label={`Delete ${item.name}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Inner content ──────────────────────────────────────────────────────────

function OrgInventoryContent({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const { adminOrg } = useUserProfile();
  const { items, loading, error, createItem, updateItem, deleteItem, syncFromMaster } =
    useOrgInventory(orgId);
  const { rooms } = useOrgResidentialRooms(orgId);

  const commercialEnabled = adminOrg?.featureCommercialMovingQuotes !== false;
  const residentialEnabled = adminOrg?.featureResidentialMovingQuotes ?? false;
  const bothEnabled = commercialEnabled && residentialEnabled;
  // Default to commercial unless only residential is enabled
  const defaultView: QuoteType = !commercialEnabled && residentialEnabled ? 'residential' : 'commercial';

  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<QuoteType>(defaultView);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<ResidentialItemType | 'all'>('all');
  const [residentialTypeFilter, setResidentialTypeFilter] = useState<ResidentialItemType | 'all'>('all');
  const [roomFilter, setRoomFilter] = useState<string>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<OrgInventoryItem | null>(null);
  const [editSection, setEditSection] = useState<'addEquipment' | undefined>(undefined);
  // Cards or table; remembered across visits.
  const [listView, setListView] = useState<InventoryView>(readStoredInventoryView);
  function switchListView(v: InventoryView) { setListView(v); storeInventoryView(v); }
  function openEdit(item: OrgInventoryItem, section?: 'addEquipment') { setEditSection(section); setEditingItem(item); }
  const [deletingItem, setDeletingItem] = useState<OrgInventoryItem | null>(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ added: number; updated: number } | null>(null);
  const [syncError, setSyncError] = useState('');

  const isCommercialView = viewMode === 'commercial';

  // Type / category / room filters live in a sheet; only active ones show as pills.
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const inventoryFilters: InventoryFilters = {
    typeFilter,
    categoryFilter: isCommercialView ? categoryFilter : residentialTypeFilter,
    roomFilter,
  };
  const activeFilterCount = countActiveInventoryFilters(inventoryFilters, viewMode);
  function applyInventoryFilters(f: InventoryFilters) {
    setTypeFilter(f.typeFilter);
    if (isCommercialView) setCategoryFilter(f.categoryFilter); else setResidentialTypeFilter(f.categoryFilter);
    setRoomFilter(f.roomFilter);
  }
  const viewItems = items.filter(item =>
    isCommercialView
      ? (item.quoteType ?? 'commercial') === 'commercial'
      : item.quoteType === 'residential',
  );

  const filtered = viewItems
    .filter(item => {
      const matchesSearch =
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.description?.toLowerCase().includes(search.toLowerCase());
      if (!matchesSearch) return false;
      if (isCommercialView) {
        const matchesType = typeFilter === 'all' || item.type === typeFilter;
        const matchesCategory = categoryFilter === 'all' || item.residentialType === categoryFilter;
        return matchesType && matchesCategory;
      }
      const matchesResType = residentialTypeFilter === 'all' || item.residentialType === residentialTypeFilter;
      const matchesRoom = roomFilter === 'all' || item.roomId === roomFilter;
      return matchesResType && matchesRoom;
    })
    .sort((a, b) => {
      if (isCommercialView) {
        const typeDiff = (TYPE_ORDER[a.type as ItemType] ?? 99) - (TYPE_ORDER[b.type as ItemType] ?? 99);
        return typeDiff !== 0 ? typeDiff : a.name.localeCompare(b.name);
      }
      return a.name.localeCompare(b.name);
    });

  // Group sorted items by type for section headers (commercial only)
  const groups: { type: string; items: OrgInventoryItem[] }[] = [];
  if (isCommercialView) {
    for (const item of filtered) {
      const last = groups[groups.length - 1];
      if (last && last.type === item.type) {
        last.items.push(item);
      } else {
        groups.push({ type: item.type as ItemType, items: [item] });
      }
    }
  }

  async function handleSync() {
    setSyncing(true);
    setSyncError('');
    setSyncResult(null);
    try {
      const result = await syncFromMaster();
      setSyncResult(result);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Restore failed.');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="px-4 pt-4 pb-8 md:px-8 md:pt-6 md:max-w-7xl md:mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-small mb-4 -ml-1">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Admin Panel
        </button>
        <ChevronRight size={14} className="text-stone" />
        <button
          type="button"
          onClick={() => navigate('/admin/quoting')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Quoting
        </button>
        <ChevronRight size={14} className="text-stone" />
        <span className="text-midnight font-medium">Inventory Items</span>
      </nav>

      <h1 className="font-display text-h1 text-midnight mb-3 md:mb-1">Inventory Items</h1>
      <p className="hidden md:block text-[13px] text-slate mb-5 leading-snug">
        Your organization's catalog of inventory items used in estimates.
      </p>

      {/* Sync result / error banners */}
      {syncResult && (
        <div className="mb-4 flex items-start gap-3 px-4 py-3 rounded-xl bg-plum/8 border border-plum/20">
          <div className="flex-1 min-w-0">
            <p className="text-small font-semibold text-midnight">
              Restored from system list —{' '}
              {[
                syncResult.added > 0 && `${syncResult.added} added`,
                syncResult.updated > 0 && `${syncResult.updated} updated`,
              ]
                .filter(Boolean)
                .join(', ') || 'already up to date'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSyncResult(null)}
            className="shrink-0 w-6 h-6 flex items-center justify-center text-slate hover:text-midnight"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {syncError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-small text-rose">{syncError}</p>
          </div>
          <button
            type="button"
            onClick={() => setSyncError('')}
            className="shrink-0 w-6 h-6 flex items-center justify-center text-rose"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search items…"
            className="w-full h-11 pl-9 pr-3 rounded-xl border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>

        {/* Filter */}
        <button
          type="button"
          onClick={() => setShowFilterSheet(true)}
          className={`relative shrink-0 h-11 w-11 rounded-xl border flex items-center justify-center
            transition-colors duration-150
            ${activeFilterCount > 0
              ? 'border-plum bg-plum/5 text-plum'
              : 'border-stone bg-white text-slate hover:text-midnight hover:border-plum/40'}`}
          aria-label="Filter items"
        >
          <Filter size={16} />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-plum
                             flex items-center justify-center text-[9px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Restore from System List */}
        <button
          type="button"
          onClick={() => setConfirmRestore(true)}
          disabled={syncing}
          className="shrink-0 h-11 px-3 rounded-xl border border-stone bg-white
                     flex items-center gap-1.5
                     text-[13px] font-semibold text-slate
                     hover:border-plum hover:text-plum active:bg-mist disabled:opacity-50
                     transition-colors duration-150"
          title="Restore from System List"
        >
          {syncing ? <LoadingSpinner size="sm" /> : <RefreshCw size={15} />}
          <span className="hidden sm:inline">Restore</span>
        </button>

        {/* Add Item */}
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="shrink-0 h-11 w-11 rounded-xl bg-plum text-white
                     flex items-center justify-center
                     hover:bg-rose active:opacity-80
                     transition-colors duration-150"
          aria-label="Add inventory item"
        >
          <Plus size={20} />
        </button>
      </div>

      {/* Commercial / Residential toggle (only when both enabled) + cards / table switch */}
      <div className={`flex items-stretch gap-2 mb-2 md:mb-4 ${bothEnabled ? '' : 'justify-end'}`}>
        {bothEnabled && (
          <div className="flex-1 flex bg-mist rounded-lg p-0.5 gap-1 md:p-1">
            {(['commercial', 'residential'] as const).map(qt => (
              <button
                key={qt}
                type="button"
                onClick={() => { setViewMode(qt); setSearch(''); }}
                className={`flex-1 h-11 md:h-auto md:py-2.5 text-[13px] md:text-sm font-semibold rounded-md transition-all duration-150
                  ${viewMode === qt
                    ? 'bg-white text-midnight shadow-brand-sm'
                    : 'text-slate hover:text-midnight'
                  }`}
              >
                {qt === 'commercial' ? 'Commercial' : 'Residential'}
              </button>
            ))}
          </div>
        )}
        <InventoryViewSwitch value={listView} onChange={switchListView} />
      </div>

      {/* Active filters — only the ones narrowing the view */}
      <ActiveInventoryFilterPills
        viewMode={viewMode}
        filters={inventoryFilters}
        rooms={rooms}
        onChange={applyInventoryFilters}
      />

      {showFilterSheet && (
        <InventoryFilterSheet
          viewMode={viewMode}
          filters={inventoryFilters}
          typeOptions={ITEM_TYPES}
          rooms={rooms}
          onApply={applyInventoryFilters}
          onClose={() => setShowFilterSheet(false)}
        />
      )}

      {/* List */}
      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load inventory</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="flex flex-col items-center py-16 gap-3 text-center">
          <div className="w-14 h-14 rounded-full bg-mist flex items-center justify-center">
            <Package size={24} className="text-slate" />
          </div>
          <p className="text-small text-slate">No items yet. Add one or restore from the system list.</p>
        </div>
      )}

      {!loading && !error && items.length > 0 && filtered.length === 0 && (
        <div className="flex flex-col items-center py-12 gap-2 text-center">
          <p className="text-small text-slate">No items match your search.</p>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <>
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3 md:mb-4">
            {filtered.length} item{filtered.length !== 1 ? 's' : ''}
            {search && ` matching "${search}"`}
          </p>
          {listView === 'table' ? (
            <InventoryTable
              items={filtered}
              viewMode={viewMode}
              typeOptions={ITEM_TYPES}
              onUpdate={(item, patch) => updateItem(item.id, patch)}
              onEdit={openEdit}
              filters={inventoryFilters}
              onFiltersChange={applyInventoryFilters}
            />
          ) : isCommercialView ? (
            <div className="flex flex-col gap-6">
              {groups.map(group => (
                <section key={group.type}>
                  <div className="flex items-center gap-3 mb-3">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate shrink-0">
                      {group.type}
                    </p>
                    <div className="flex-1 h-px bg-stone" />
                    <span className="shrink-0 text-[11px] text-slate">{group.items.length}</span>
                  </div>
                  <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {group.items.map(item => (
                      <li key={item.id}>
                        <InventoryItemCard
                          item={item}
                          orgId={orgId}
                          onEdit={() => setEditingItem(item)}
                          onDelete={() => setDeletingItem(item)}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          ) : (
            <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map(item => (
                <li key={item.id}>
                  <InventoryItemCard
                    item={item}
                    orgId={orgId}
                    rooms={rooms}
                    onEdit={() => setEditingItem(item)}
                    onDelete={() => setDeletingItem(item)}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Add form */}
      {showAddForm && (
        <InventoryItemForm
          orgId={orgId}
          allItems={items}
          rooms={rooms}
          onClose={() => setShowAddForm(false)}
          onSave={createItem}
        />
      )}

      {/* Edit form */}
      {editingItem && (
        <InventoryItemForm
          initial={editingItem}
          orgId={orgId}
          allItems={items}
          rooms={rooms}
          scrollTo={editSection}
          onClose={() => { setEditingItem(null); setEditSection(undefined); }}
          onSave={input => updateItem(editingItem.id, input)}
        />
      )}

      {/* Delete confirm */}
      <ConfirmModal
        isOpen={!!deletingItem}
        title="Delete Item"
        message={`Are you sure you want to delete "${deletingItem?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deletingItem) deleteItem(deletingItem.id);
          setDeletingItem(null);
        }}
        onClose={() => setDeletingItem(null)}
      />

      {/* Restore from system list confirm */}
      <ConfirmModal
        isOpen={confirmRestore}
        title="Restore from System List"
        message="This will update all system-sourced items with the latest values from the master inventory list, and add any new master items that aren't in your list yet. Items your organization added won't be affected."
        confirmLabel="Restore"
        variant="primary"
        onConfirm={handleSync}
        onClose={() => setConfirmRestore(false)}
      />
    </div>
  );
}

// ── Page (guards org admin access) ────────────────────────────────────────

export function OrgInventoryPage() {
  const { adminOrg, loading } = useUserProfile();

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!adminOrg) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-3 text-center">
        <p className="text-small text-slate">You don't have org admin access.</p>
      </div>
    );
  }

  return <OrgInventoryContent orgId={adminOrg.orgId} />;
}
